import {Desk, EmployeeRef, Floor, INeighbourhood, Neighbourhood} from "../../api/Model";
import {associateBy, getOrSet} from "../../util/MapUtil";
import {idFn} from "../../util/FunctionUtil";
import {naturalCmpOptions, stringCmp} from "../../util/SortUtil";
import {Opt} from "../../util/Optional";
import {ReactNode} from "react";
import {EmployeeNeighbourhood} from "../../api/EmployeeNeighbourhoodStore";
import {ResidenceModel} from "../../api/ResidenceStore";
import {WorkSites} from "../../api/WorkSiteMapStore";
import {AssignedDesks} from "../../api/AssignedDeskStore";
import {EmployeeVisitSheet} from "../../api/EmployeeVisitStore";
import {EmployeeVisitFigure, workDayVisitCounter} from "../employee-visit-report/EmployeeVisitReportFilter";
import {emptyEmployeeVisitSheet} from "../../api/Empty";
import {mapNotNull} from "../../util/ArrayUtil";
import {isWeekend} from "../../util/DateUtil";


export type DeskTableRow = {
    type: 'desk',
    key: string,
    desk: Desk,
} | {
    type: 'floor',
    key: string,
    name: string,
} | {
    type: 'room',
    key: string,
    name: string,
};

export abstract class DeskModel {
    private floorsById: Map<string | number, Floor>;
    private employeeVisitStatsById: ReadonlyMap<EmployeeRef['id'], EmployeeVisitFigure>;
    private readonly hasWorkDays: boolean;
    readonly visitWorkDayCount: number;
    readonly visitDates: string[];

    protected constructor(
        readonly floors: Floor[],
        readonly residencyModel: ResidenceModel,
        readonly assignedDesks: AssignedDesks,
        readonly workSites: WorkSites,
        employeeVisits: EmployeeVisitSheet,
    ) {
        this.visitDates = employeeVisits.dates;
        this.floorsById = associateBy(floors, idFn);
        const dayVisitCounter = workDayVisitCounter(employeeVisits.dates);
        this.employeeVisitStatsById = new Map(
            mapNotNull(
                employeeVisits.rows,
                row => {
                    const visitCount = dayVisitCounter(row);
                    return [
                        row.value.id,
                        visitCount,
                    ];
                }
            )
        );
        this.visitWorkDayCount = employeeVisits.dates.filter(date => !isWeekend(date)).length;
        this.hasWorkDays = this.visitWorkDayCount > 0;
    }

    abstract employee(desk: Desk): Opt<EmployeeRef>;

    employeeVisitStats(desk: Desk): Opt<{employee: EmployeeRef, stats: EmployeeVisitFigure}> {
        if (!this.hasWorkDays) return undefined;

        const employee = this.employee(desk);
        if (!employee) return undefined;

        const stats = this.employeeVisitStatsById.get(employee.id);
        if (!stats) return undefined;

        return {
            employee,
            stats,
        };
    }

    employeeVisitStatsFor(employee: EmployeeRef): Opt<EmployeeVisitFigure> {
        if (!this.hasWorkDays) return undefined;
        return this.employeeVisitStatsById.get(employee.id);
    }

    abstract neighbourhood(desk: Desk): Opt<INeighbourhood>;

    abstract employeeNeighbourhoods(employee: EmployeeRef): readonly INeighbourhood[];

    abstract neighbourhoodEmployees(neighbourhood: INeighbourhood): readonly EmployeeRef[];

    floor(desk: Desk): Opt<Floor> {
        return desk.floorId != null ? this.floorsById.get(desk.floorId) : undefined;
    }

    isResident(desk: Desk): boolean {
        const employee = this.employee(desk);
        return employee == null || this.residencyModel.matchResidence(this.workSites.residenceByEmployee(employee));
    }

    isDuplicatedDesk(desk: Desk): boolean {
        const employee = this.employee(desk);
        return employee != null && this.assignedDesks.desksOf(employee).length > 1;
    }

    abstract actionButton(desk: Desk): ReactNode;

    neighbourhoodMismatch(desk: Desk): Opt<{
        deskNeighbourhood?: INeighbourhood,
        employeeNeighbourhoods: readonly INeighbourhood[],
    }> {
        const employee = this.employee(desk);
        if (!employee) return undefined;

        const deskNeighbourhood = this.neighbourhood(desk);
        const employeeNeighbourhoods = this.employeeNeighbourhoods(employee);

        if (deskNeighbourhood) {
            if (!employeeNeighbourhoods.some(eHood => eHood.key === deskNeighbourhood.key)) {
                return {
                    deskNeighbourhood: deskNeighbourhood,
                    employeeNeighbourhoods: employeeNeighbourhoods,
                };
            } else {
                return undefined;
            }
        } else if (employeeNeighbourhoods.length > 0) {
            return {
                employeeNeighbourhoods: employeeNeighbourhoods,
            };
        } else {
            return undefined;
        }
    }

    static rows(desks: Desk[], floors: Floor[]): DeskTableRow[] {
        const floorsById = associateBy(floors, idFn);
        const grouped = new Map<string, {
            key: string,
            name: string,
            rooms: Map<string, {
                key: string,
                name: string,
                desks: Desk[],
            }>,
        }>();

        desks.forEach(desk => {
            const floorKey = desk.floorId ? `id:${desk.floorId}` : 'no-floor';
            const floorGroup = getOrSet(grouped, floorKey, () => ({
                key: floorKey,
                name: desk.floorId
                    ? floorsById.get(desk.floorId)?.name ?? 'Unknown floor'
                    : 'No floor',
                rooms: new Map<string, {
                    key: string,
                    name: string,
                    desks: Desk[],
                }>(),
            }));

            const roomKey = desk.roomName || 'unknown-room';
            const roomGroup = getOrSet(floorGroup.rooms, roomKey, () => ({
                key: roomKey,
                name: desk.roomName || 'Unknown room',
                desks: [],
            }));

            roomGroup.desks.push(desk);
        });

        return [...grouped.values()]
            .sort(stringCmp('name', true, naturalCmpOptions))
            .flatMap(floor => [
                {
                    type: 'floor' as const,
                    key: `floor-${floor.key}`,
                    name: floor.name,
                },
                ...[...floor.rooms.values()]
                    .sort(stringCmp('name', false, naturalCmpOptions))
                    .flatMap(room => [
                        {
                            type: 'room' as const,
                            key: `room-${floor.key}-${room.key}`,
                            name: room.name,
                        },
                        ...room.desks
                            .sort(stringCmp('name', false, naturalCmpOptions))
                            .map(desk => ({
                                type: 'desk' as const,
                                key: `desk-${desk.id}`,
                                desk,
                            })),
                    ]),
            ]);
    }
}

export class DeskModelImpl extends DeskModel {
    private neighbourhoodsById: ReadonlyMap<ReturnType<typeof idFn>, Neighbourhood>;
    private employeeNeighbourhood: EmployeeNeighbourhood<Neighbourhood>;

    constructor(
        floors: Floor[],
        neighbourhoods: Neighbourhood[],
        residencyModel: ResidenceModel,
        assignedDesks: AssignedDesks,
        workSites: WorkSites,
        employeeVisits: EmployeeVisitSheet,
    ) {
        super(floors, residencyModel, assignedDesks, workSites, employeeVisits);
        this.neighbourhoodsById = associateBy(neighbourhoods, idFn);
        this.employeeNeighbourhood = EmployeeNeighbourhood.from(neighbourhoods);
    }

    employee(desk: Desk): Opt<EmployeeRef> {
        return this.assignedDesks.assigneeOf(desk);
    }

    neighbourhood(desk: Desk): Neighbourhood | undefined {
        if (!desk.neighbourhoodId) return undefined;
        return this.neighbourhoodsById.get(desk.neighbourhoodId);
    }

    employeeNeighbourhoods(employee: EmployeeRef): readonly Neighbourhood[] {
        return this.employeeNeighbourhood.get(employee);
    }

    neighbourhoodEmployees(neighbourhood: INeighbourhood): readonly EmployeeRef[] {
        return (neighbourhood as Neighbourhood).employees ?? [];
    }

    actionButton(_: Desk): ReactNode {
        return undefined;
    }

    static EMPTY = new DeskModelImpl([],
        [],
        ResidenceModel.EMPTY,
        AssignedDesks.EMPTY,
        WorkSites.EMPTY,
        emptyEmployeeVisitSheet,
    );
}
